import React, { FunctionComponent, ComponentType, useEffect } from 'react';
import {
  Route,
  Redirect,
  withRouter,
  RouteProps,
  match,
} from 'react-router-dom';
import ReactGA from 'react-ga4';
import { useQuery } from 'react-apollo';
import { path, pathOr, propOr } from 'ramda';
import { compose } from 'recompose';

import { useUserMgmt } from 'components/UserMgmt/UserMgmt.context';
import { GET_PROFILE } from 'components/UserMgmt/UserMgmt.queries';
import Loading from 'components/Loading';
import { Profile } from 'components/UserMgmt/UserMgmt.helpers';
import { ModalProvider } from 'components/Modal/modalContext';

type Props = {
  component: ComponentType;
  computedMatch: match;
} & RouteProps;

const renderLogout = () => <Redirect to="/logout" />;

const ProtectedRoute: FunctionComponent<Props> = (props) => {
  const {
    isAuthenticated,
    isLoading,
    checkStorage,
    isExternalUser,
  } = useUserMgmt();
  const { data, loading } = useQuery(GET_PROFILE);
  const userProfile = propOr({}, 'userProfile', data) as Profile;

  useEffect(() => {
    checkStorage();
  });

  useEffect(() => {
    const page = pathOr('', ['location', 'pathname'], props);

    ReactGA.set({ page });
    ReactGA.set({ userId: userProfile.sub });
  });

  if (isExternalUser && !loading) {
    const merchantName = path(
      ['computedMatch', 'params', 'merchantName'],
      props,
    );

    if (merchantName && userProfile.merchantName !== merchantName) {
      // external users don't have this route
      return <Redirect to="/" />;
    }
  }

  const {
    component: WrappedComponent,
    render: componentRender,
    ...rest
  } = props;

  const renderRoute = (props: any) => (
    <ModalProvider>
      <WrappedComponent {...rest} {...props} />
    </ModalProvider>
  );
  const render = isAuthenticated
    ? componentRender || renderRoute
    : renderLogout;

  if (isLoading) return <Loading loading />;

  return <Route {...rest} render={render} />;
};

export default compose<Props, Partial<Props>>(withRouter)(ProtectedRoute);
